"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { Text, clx, useToggleState } from "@medusajs/ui"
import { Fragment } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { HttpTypes } from "@medusajs/types"
import { Locale } from "@lib/data/locales"
import type { LegalContent } from "@lib/content-defaults"
import SocialLinks from "../social-links"

// key 保留英文當識別碼(data-testid 用),label 才是顯示文字。
// 原本是 { Home: "/" } 這種寫法,key 同時當顯示文字與 testid,
// 直接改成中文會讓 testid 變成「首頁-link」,所以拆成兩個欄位。
const SideMenuItems: Record<string, { label: string; href: string }> = {
  home: { label: "首頁", href: "/" },
  store: { label: "全部商品", href: "/store" },
  cooking: { label: "簡單好料理", href: "/store?category=簡單好料理" },
  movement: { label: "居家輕運動", href: "/store?category=居家輕運動" },
  care: { label: "日常照顧", href: "/store?category=日常照顧" },
  accessories: { label: "實用搭配", href: "/store?category=實用搭配" },
  account: { label: "帳號", href: "/account" },
  cart: { label: "購物車", href: "/cart" },
}

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  /**
   * 品牌名。這是 client component,讀不到內容層(那要 fs),
   * 所以由 server 端的 Nav 取好傳進來。
   */
  brand: string
  /** 官方社群連結,同樣由 Nav 取好傳進來 */
  social: LegalContent["social"]
}

const SideMenu = ({ regions, locales, currentLocale, brand, social }: SideMenuProps) => {
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  className="relative h-full flex items-center transition-all ease-out duration-200 focus:outline-none hover:text-ui-fg-base"
                >
                  選單
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="fixed inset-0 z-[50] bg-black/0 pointer-events-auto"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100 backdrop-blur-2xl"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 backdrop-blur-2xl"
                leaveTo="opacity-0"
              >
                <PopoverPanel className="flex flex-col absolute w-full pr-4 sm:pr-0 sm:w-1/3 2xl:w-1/4 sm:min-w-min h-[calc(100dvh-6rem)] overflow-y-auto z-[51] inset-x-0 text-sm text-ui-fg-on-color m-2 backdrop-blur-2xl">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex flex-col h-full bg-[#242823] rounded-rounded justify-between p-6"
                  >
                    <div className="flex justify-end" id="xmark">
                      <button aria-label="關閉選單" data-testid="close-menu-button" onClick={close}>
                        <XMark />
                      </button>
                    </div>
                    <ul className="flex flex-col gap-3 items-start justify-start">
                      {Object.entries(SideMenuItems).map(([id, item]) => {
                        return (
                          <li key={id}>
                            <LocalizedClientLink
                              href={item.href}
                              className="text-xl leading-8 hover:text-ui-fg-disabled"
                              onClick={close}
                              data-testid={`${id}-link`}
                            >
                              {item.label}
                            </LocalizedClientLink>
                          </li>
                        )
                      })}
                    </ul>
                    <div className="flex flex-col gap-y-6">
                      {/* 手機頁首放不下社群圖示,在選單裡補上 */}
                      <div className="flex flex-col gap-y-3 mt-8" data-testid="side-menu-social">
                        <span className="txt-compact-small-plus">追蹤我們</span>
                        <SocialLinks social={social} variant="label" />
                      </div>
                      {!!locales?.length && (
                        <div
                          className="flex justify-between"
                          onMouseEnter={languageToggleState.open}
                          onMouseLeave={languageToggleState.close}
                        >
                          <LanguageSelect
                            toggleState={languageToggleState}
                            locales={locales}
                            currentLocale={currentLocale}
                          />
                          <ArrowRightMini
                            className={clx(
                              "transition-transform duration-150",
                              languageToggleState.state ? "-rotate-90" : ""
                            )}
                          />
                        </div>
                      )}
                      <div
                        className="flex justify-between"
                        onMouseEnter={countryToggleState.open}
                        onMouseLeave={countryToggleState.close}
                      >
                        {regions && (
                          <CountrySelect
                            toggleState={countryToggleState}
                            regions={regions}
                          />
                        )}
                        <ArrowRightMini
                          className={clx(
                            "transition-transform duration-150",
                            countryToggleState.state ? "-rotate-90" : ""
                          )}
                        />
                      </div>
                      <Text className="flex justify-between txt-compact-small">
                        © {new Date().getFullYear()} {brand}. 版權所有。
                      </Text>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
